import React, { useState, useRef, useEffect } from 'react';
import { Loader2, Check, X } from 'lucide-react';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';

export const EditableTextField = ({
  label,
  value,
  onSave,
  placeholder = 'Click to edit',
  className = '',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [isLoading, setIsLoading] = useState(false);
  const [showCheckmark, setShowCheckmark] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing) {
      setInputValue(value);
      inputRef.current?.focus();
    }
  }, [isEditing, value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsEditing(false);
      }
    };
    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditing]);

  const handleSave = async () => {
    if (inputValue !== value) {
      setIsLoading(true);
      await onSave(inputValue);
      setIsLoading(false);
      setShowCheckmark(true);
      setTimeout(() => setShowCheckmark(false), 1500);
    }
    setIsEditing(false);
  };

  const handleKeyDown = async (e) => {
    if (e.key === 'Enter') await handleSave();
    if (e.key === 'Escape') setIsEditing(false);
  };

  const handleCancel = () => {
    setInputValue(value);
    setIsEditing(false);
  };

  return (
    <div className={clsx('relative w-full', className)} ref={wrapperRef}>
      {label && (
        <label className="block text-sm text-white/70 mb-1">{label}</label>
      )}

      <AnimatePresence mode="wait">
        {isEditing ? (
          <motion.div
            key="input"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.2 }}
            className="relative flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-white/10 dark:bg-white/5 border border-white/20 backdrop-blur-md shadow-inner"
          >
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-transparent text-white placeholder-white/40 focus:outline-none"
              placeholder={placeholder}
            />

            {isLoading && (
              <Loader2 className="animate-spin text-white w-5 h-5" />
            )}

            {/* Floating Buttons */}
            <div className="absolute -bottom-5 right-2 flex space-x-2 bg-transparent">
              <button
                onClick={handleSave}
                className="p-1 rounded-full bg-green-500 hover:bg-green-600 text-white"
              >
                <Check size={16} />
              </button>
              <button
                onClick={handleCancel}
                className="p-1 rounded-full bg-red-500 hover:bg-red-600 text-white"
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="display"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 cursor-pointer group hover:bg-white/10 rounded"
          >
            <span className="text-white text-md truncate">
              {value || (
                <span className="italic text-white/40">{placeholder}</span>
              )}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ✅ Animated Checkmark */}
      <AnimatePresence>
        {showCheckmark && (
          <motion.div
            key="check"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1.2, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            className="mt-2 text-green-400 flex items-center space-x-1"
          >
            <Check size={16} />
            <span className="text-xs">Saved</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
